/**
 * Forensic-trail платежей.
 *
 * PaymentEvent — каждый шаг lifecycle платежа (intent, webhook, confirm,
 * refund, fiscal-*) одной строкой для админ-UI timeline.
 * WebhookLog — сырые тела входящих webhook'ов до парсинга, с результатом
 * валидации подписи; для восстановления при инцидентах.
 *
 * Источник правды: drizzle/0104_payment_events_and_webhook_logs.sql.
 */
import {
  bigserial,
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { order, payment } from './commerce';
import { adminUser } from './admin';

export const paymentEventSource = pgEnum('PaymentEventSource', [
  'CLOUDPAYMENTS',
  'MULTICARD',
  'CLICK',
  'PAYME',
  'RAHMAT',
  'BUSINESS_RU',
  'INTERNAL',
  'ADMIN',
]);

export const paymentEventType = pgEnum('PaymentEventType', [
  'CREATED',
  'INTENT_CREATED',
  'INTENT_REUSED',
  'WEBHOOK_RECEIVED',
  'CHECK_OK',
  'CHECK_REJECTED',
  'AWAITING_3DS',
  'CONFIRMED',
  'FAILED',
  'CANCELLED',
  'REFUND_REQUESTED',
  'REFUND_SUCCESS',
  'REFUND_FAILED',
  'TOKEN_SAVED',
  'RENEWAL_CHARGED',
  'FISCAL_ENQUEUED',
  'FISCAL_SENT',
  'FISCAL_POLLED',
  'FISCAL_SUCCESS',
  'FISCAL_FAILED',
  'FISCAL_CALLBACK_RECEIVED',
  'ADMIN_RETRY',
  'NOTE',
]);

export const paymentEventLevel = pgEnum('PaymentEventLevel', [
  'INFO',
  'WARN',
  'ERROR',
]);

export const paymentEvent = pgTable(
  'PaymentEvent',
  {
    id: bigserial('id', { mode: 'number' }).primaryKey(),
    paymentId: integer('paymentId').references(() => payment.id, {
      onDelete: 'set null',
      onUpdate: 'cascade',
    }),
    orderId: integer('orderId').references(() => order.id, {
      onDelete: 'set null',
      onUpdate: 'cascade',
    }),
    source: paymentEventSource('source').notNull(),
    type: paymentEventType('type').notNull(),
    level: paymentEventLevel('level').default('INFO').notNull(),
    message: text('message').notNull(),
    payload: jsonb('payload'),
    actorAdminId: integer('actorAdminId').references(() => adminUser.id, {
      onDelete: 'set null',
      onUpdate: 'cascade',
    }),
    durationMs: integer('durationMs'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('PaymentEvent_paymentId_createdAt_idx').on(
      table.paymentId,
      table.createdAt,
    ),
    index('PaymentEvent_orderId_createdAt_idx').on(
      table.orderId,
      table.createdAt,
    ),
    index('PaymentEvent_type_createdAt_idx').on(table.type, table.createdAt),
    index('PaymentEvent_level_createdAt_idx')
      .on(table.level, table.createdAt)
      .where(sql`"level" != 'INFO'`),
  ],
);

export const webhookProvider = pgEnum('WebhookProvider', [
  'CLOUDPAYMENTS',
  'MULTICARD',
  'CLICK',
  'PAYME',
  'RAHMAT',
  'BUSINESS_RU',
]);

export const webhookLog = pgTable(
  'WebhookLog',
  {
    id: bigserial('id', { mode: 'number' }).primaryKey(),
    provider: webhookProvider('provider').notNull(),
    endpoint: text('endpoint').notNull(),
    method: text('method').notNull(),
    ip: text('ip'),
    userAgent: text('userAgent'),
    headers: jsonb('headers'),
    signatureHeader: text('signatureHeader'),
    rawBody: text('rawBody'),
    hmacVerified: boolean('hmacVerified'),
    parsedOk: boolean('parsedOk'),
    errorText: text('errorText'),
    responseStatus: integer('responseStatus'),
    responseBody: text('responseBody'),
    durationMs: integer('durationMs'),
    paymentId: integer('paymentId').references(() => payment.id, {
      onDelete: 'set null',
      onUpdate: 'cascade',
    }),
    orderId: integer('orderId').references(() => order.id, {
      onDelete: 'set null',
      onUpdate: 'cascade',
    }),
    receivedAt: timestamp('receivedAt', { precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('WebhookLog_provider_receivedAt_idx').on(
      table.provider,
      table.receivedAt,
    ),
    index('WebhookLog_paymentId_idx').on(table.paymentId),
    index('WebhookLog_orderId_idx').on(table.orderId),
    index('WebhookLog_hmacVerified_idx')
      .on(table.hmacVerified, table.receivedAt)
      .where(sql`"hmacVerified" = false`),
  ],
);
